import { api } from 'common';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { Lock, Email } from "@mui/icons-material";
import React, { useState, useEffect } from 'react';
import { useThemeApp } from 'components/Theme/theme';
import { useSelector, useDispatch } from "react-redux";
import { useAlert } from 'components/alert/AlertContext';
import InputAdornment from "@mui/material/InputAdornment";
import { Input, Stack, Box, Typography, Paper, Button } from "@mui/material";

const testValidEmail = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export default function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const { colors } = useThemeApp();
  const auth = useSelector(state => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const { sendResetMail, verifyEmailPassword } = api;
  const settings = useSelector(state => state.settingsdata.settings);

  const logoSrc = settings?.logo || "";
  const fallbackLogo = `${process.env.PUBLIC_URL}/icon-big.png`;

  const [data, setData] = React.useState({
    password: undefined,
    email: undefined
  });

  useEffect(() => {
    if (auth.profile) {
      if (auth.profile.uid) {
        let role = auth.profile.usertype;
        if (role === 'admin' || role === 'fleetadmin') {
          navigate('/dashboard');
        }
        else if (role === 'driver') {
          navigate('/bookings');
        }
        else {
          navigate('/');
        }
      }
    }
    if (auth.error && auth.error.flag && auth.error?.msg?.message !== t('not_logged_in')) {
      if (auth.error?.msg?.message === t('require_approval')) {
        showAlert({ type: "ERROR", message: t('require_approval') })
      } else if (auth.error?.msg?.message === "Firebase: Error (auth/invalid-verification-code).") {
        showAlert({ type: "ERROR", message: t('login_error') })
        setIsLoading(false);
      } else {
        if (data.email && (data.email === '' || !(!data.email))) {
          showAlert({ type: "ERROR", message: t('login_error') })
          setIsLoading(false);
        }
      }
    }
  }, [auth.profile, auth.error, auth.verificationId, navigate, data, data.email, data.email, t, setData]);

  const handleLogin = async (e: any) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      if (!data.password || !data.email) {
        showAlert({
          type: 'ERROR',
          title: t("PLEASE_FILL_IN_ALL_THE_INFORMATION")
        })
        setIsLoading(false);
        return;
      }

      if (testValidEmail.test(data.email!)) {
        dispatch(verifyEmailPassword(
          data.email!,
          data.password!,
        ));
      } else {
        showAlert({
          type: 'ERROR',
          title: t('PROPER_EMAIL')
        })
        setIsLoading(false);
      }
    } catch {
      showAlert({
        type: 'ERROR',
        title: t('error')
      })
      setIsLoading(false);
    }

  }

  const forgotPassword = async () => {
    if (data.email && testValidEmail.test(data.email)) {
      await dispatch(sendResetMail(data.email));
      showAlert({
        type: 'SUCCESS',
        title: t('PASSWORD_RESET_LINK_SENT_TO_YOUR_EMAIL')
      })
    } else {
      showAlert({
        type: 'ERROR',
        title: t('PROPER_EMAIL')
      })
    }
  };

  return (
    <>
      <Box
        sx={{
          width: '100vw',
          display: 'flex',
          overflow: 'hidden',
          minHeight: '100vh',
          position: 'relative',
          alignItems: 'center',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: { xs: 2, sm: 3 },
          background: `linear-gradient(135deg, ${colors.primary}20 0%, ${colors.background} 100%)`,
          '&::before': {
            content: '""',
            zIndex: 0,
            top: '-100px',
            width: '300px',
            right: '-100px',
            height: '300px',
            borderRadius: '50%',
            position: 'absolute',
            background: `linear-gradient(45deg, ${colors.primary}30, transparent)`,
          },
          '&::after': {
            content: '""',

            zIndex: 0,
            left: '-50px',
            width: '200px',
            bottom: '-50px',
            height: '200px',
            borderRadius: '50%',
            position: 'absolute',
            background: `linear-gradient(45deg, transparent, ${colors.primary}20)`,
          }
        }}
      >

        <Box
          sx={{
            zIndex: 10,
            width: 'auto',
            display: 'flex',
            aspectRatio: 1,
            borderRadius: 3,
            userSelect: 'none',
            position: 'absolute',
            alignItems: 'center',
            background: colors.card,
            justifyContent: 'center',
            backdropFilter: 'blur(20px)',
            padding: { xs: '10px', sm: '15px' },
            border: `1px solid ${colors.border}`,
            boxShadow: `0 4px 20px ${colors.shadow}`,
            top: { xs: '20px', sm: '30px', md: '40px' },
            left: { xs: '20px', sm: '30px', md: '40px' },
            height: { xs: '60px', sm: '70px', md: '80px' },
          }}
        >
          <Box
            alt="logo"
            component="img"
            src={logoSrc || fallbackLogo}
            sx={{
              height: '100%',
              width: 'auto',
              maxWidth: '100%',
              objectFit: 'contain',
              filter: colors.primary ? `drop-shadow(0 0 10px ${colors.primary}30)` : 'none',
            }}
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            zIndex: 1,
            width: '100%',
            borderRadius: 3,
            maxWidth: '400px',
            position: 'relative',
            background: colors.card,
            padding: { xs: 3, sm: 4 },
            transform: 'translateY(0)',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${colors.border}`,
            boxShadow: `0 8px 32px ${colors.shadow}`,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 40px ${colors.shadow}`,
            }
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            align="center"
            sx={{
              background: ` linear-gradient(45deg, ${colors.primary}, ${colors.primary}20)`,
              WebkitTextFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: colors.primary,
              marginBottom: 0.5,
              fontWeight: 700,
            }}
          >
            {t('WELCOME_BACK')}
          </Typography>

          <Typography
            variant="body2"
            align="center"
            sx={{
              color: colors.text,
              opacity: 0.7,
              marginBottom: 4,
            }}
          >
            {t('LOG_IN_TO_CONTINUE')}
          </Typography>


          <Stack spacing={3}>
            <Box>
              <Input
                fullWidth
                id="email"
                value={data.email || ''}
                placeholder={t('email_id')}
                onChange={(event) => {
                  setData({ ...data, [event.target.id]: event.target.value })
                }}
                startAdornment={
                  <InputAdornment position="start" sx={{ color: colors.placeholder, mr: 1 }}>
                    <Email />
                  </InputAdornment>
                }
                sx={{
                  color: colors.text,
                  '&::before': {
                    borderBottom: `2px solid ${colors.border} !important`,
                  },
                  '&:hover:not(.Mui-disabled):before': {
                    borderBottom: `2px solid ${colors.primary} !important`,
                  },
                  '&::after': {
                    borderBottom: `2px solid ${colors.primary}`,
                  },
                  '& input': {
                    color: colors.text,
                    padding: '12px 0',
                    '&::placeholder': {
                      color: colors.placeholder,
                      opacity: 0.7,
                    }
                  }
                }}
              />
            </Box>

            <Box>
              <Input
                fullWidth
                id="password"
                type="password"
                autoComplete="off"
                placeholder={t('password')}
                value={data.password || ''}
                onChange={(event) => {
                  setData({ ...data, [event.target.id]: event.target.value })
                }}
                startAdornment={
                  <InputAdornment position="start" sx={{ color: colors.placeholder, mr: 1 }}>
                    <Lock />
                  </InputAdornment>
                }
                sx={{
                  color: colors.text,
                  '&::before': {
                    borderBottom: `2px solid ${colors.border} !important`,
                  },
                  '&:hover:not(.Mui-disabled):before': {
                    borderBottom: `2px solid ${colors.primary} !important`,
                  },
                  '&::after': {
                    borderBottom: `2px solid ${colors.primary}`,
                  },
                  '& input': {
                    color: colors.text,
                    padding: '12px 0',
                    '&::placeholder': {
                      color: colors.placeholder,
                      opacity: 0.7,
                    }
                  }
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant="text"
                color="primary"
                onClick={forgotPassword}
                sx={{
                  fontWeight: 500,
                  color: colors.primary,
                  textTransform: 'none',
                  '&:hover': {
                    background: `${colors.primary}10`,
                  }
                }}
              >
                {t('FORGOT_PASSWORD')}
              </Button>
            </Box>

            <LoadingButton
              fullWidth
              size="large"
              color="primary"
              loading={isLoading}
              onClick={handleLogin}
              sx={{
                padding: '14px',
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '1rem',
                color: colors.white,
                textTransform: 'none',
                transition: 'all 0.3s ease',
                boxShadow: `0 4px 20px ${colors.primary}40`,
                background: `linear-gradient(45deg, ${colors.primary}, ${colors.primary}20)`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 25px ${colors.primary}60`,
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              }}
            >
              {t('login')}
            </LoadingButton>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}